import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

type Row = {
  id: string;
  state_code: string;
  channel_name: string;
  channel_id: string;
  // optional columns (may not exist in DB)
  is_default?: boolean | null;
  active?: boolean | null;
};

type Props = {
  stateCode: string;
  value: string;
  onChange: (v: string) => void;
  label?: string;
};

export default function StateDiscordChannelSelect({ stateCode, value, onChange, label }: Props) {
  const sc = String(stateCode || "").trim();
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setErr("");
      setRows([]);

      if (!sc) return;

      // IMPORTANT: select("*") prevents 400s when columns drift (active/is_default may not exist)
      const res = await supabase
        .from("state_discord_channels")
        .select("*")
        .eq("state_code", sc)
        .order("channel_name", { ascending: true });

      if (cancelled) return;

      if (res.error) {
        setErr(res.error.message);
        setRows([]);
        return;
      }

      const data = (res.data ?? []) as any[];

      // Filter out disabled channels only if 'active' exists
      const filtered = data.filter((r) => {
        if (typeof r.active === "boolean") return r.active === true;
        return true;
      }) as Row[];

      // Sort default first if is_default exists (safe)
      filtered.sort((a, b) => {
        const da = a?.is_default ? 1 : 0;
        const db = b?.is_default ? 1 : 0;
        if (db !== da) return db - da;
        return String(a.channel_name || "").localeCompare(String(b.channel_name || ""));
      });

      setRows(filtered);
    })();

    return () => {
      cancelled = true;
    };
  }, [sc]);

  const options = useMemo(() => rows, [rows]);

  return (
    <div style={{ marginTop: 10 }}>
      {label ? <div style={{ fontWeight: 900, marginBottom: 6 }}>{label}</div> : null}

      <select
        className="zombie-input"
        value={value || ""}
        onChange={(e) => onChange(String(e.target.value || ""))}
        style={{ padding: "10px 12px", width: "100%" }}
      >
        <option value="">— Choose a Discord channel —</option>
        {options.map((r) => (
          <option key={r.id} value={r.channel_id}>
            {(r.is_default ? "⭐ " : "") + String(r.channel_name || r.channel_id)}
          </option>
        ))}
      </select>

      {err ? (
        <div style={{ marginTop: 6, opacity: 0.85, fontSize: 12 }}>
          <b>StateDiscordChannelSelect load error:</b> {err}
        </div>
      ) : null}
    </div>
  );
}
